import { Flex } from '@chakra-ui/react'
import { userListContext } from 'BrokerTsLib/src/context'
import type { UserGridItem, UserPagedQuery } from 'BrokerTsLib/src/dtos'
import type { FlexGroupUser } from 'BrokerTsLib/src/dtos/FlexGroup/FlexGroupUser'
import type { Ref } from 'react'
import { forwardRef, useState } from 'react'

import { SectionHeading } from '@/components/Section/SectionHeading'
import { TableSection } from '@/components/Section/TableSection'

import type { FormSectionProps } from '../forms/FormSection'
import {
  UserList,
  UserListEmailColumn,
  UserListNameColumn,
  UserListOrganizationColumn,
  UserListRolesColumn,
  UserListTypeColumn,
  UserListValidityColumn,
} from '../user/UserList'
import { AddFlexGroupUser } from './AddFlexGroupUser'
import { RemoveButton } from './RemoveButton'

interface FlexGroupUserListSectionProps extends FormSectionProps {
  flexGroupUsers: FlexGroupUser[]
  isLoading: boolean
  title: string
  addUserAsync: (userId: string) => Promise<any>
  removeUserAsync: (userId: string) => Promise<any>
}

export const FlexGroupUserListSection = forwardRef(
  function FlexGroupUserListSection(
    {
      flexGroupUsers,
      isLoading,
      title,
      addUserAsync,
      removeUserAsync,
      ...rest
    }: FlexGroupUserListSectionProps,
    ref: Ref<HTMLDivElement>,
  ) {
    const [filter, setFilter] = useState<UserPagedQuery>({
      showInvalid: false,
      pageIndex: 1,
    })

    const organizationsFilter = (item: UserGridItem) => {
      if (!item.valid && !filter.showInvalid) return false
      if (
        filter.name &&
        !(
          `${item.firstName} ${item.lastName}`
            ?.toLowerCase()
            .includes(filter.name.toLowerCase()) ||
          item.userName?.toLowerCase().includes(filter.name.toLowerCase())
        )
      )
        return false

      return true
    }

    const users = flexGroupUsers.map((user) => user.user)

    const filteredItems = users.filter(organizationsFilter)

    return (
      <TableSection
        ref={ref}
        sectionTitle={
          <Flex
            justifyContent={'space-between'}
            alignItems={'start'}
            flexDir={{ base: 'column', sm: 'row' }}
          >
            <SectionHeading>{title}</SectionHeading>
            <AddFlexGroupUser onAddUser={addUserAsync} alreadyAdded={users} />
          </Flex>
        }
        wide
        {...rest}
      >
        <userListContext.Provider
          value={{
            loading: !!isLoading,
            totalCount: users?.length ?? 0,
            currentPage: filter.pageIndex,
            pageSize: users?.length ?? 0,
            filter,
            setCurrentPage: () => { },
            setPageSize: () => { },
            updateFilter: setFilter,
            reload: () => { },
          }}
        >
          <UserList
            users={filteredItems}
            columnsDefinition={[
              UserListTypeColumn,
              UserListNameColumn,
              UserListOrganizationColumn,
              UserListEmailColumn,
              UserListValidityColumn,
              UserListRolesColumn,
              {
                accessorKey: 'removeFromFlexGroup',
                header: '',
                cell: info => {
                  const item = info.row.original
                  return (
                    <RemoveButton onClick={() => removeUserAsync(item.userId)} />
                  )
                },
                meta: { cellProps: { p: 2 } },
              }
            ]}
          />
        </userListContext.Provider>
      </TableSection>
    )
  },
)
